import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaTrash, FaPlus, FaEye, FaHeart, FaComment, FaShare } from 'react-icons/fa';
import { LineChart, TrendingUp } from 'lucide-react';
import { getCSRFToken } from '../../utils/auth';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/pongo';


const Sparkline = ({ data, color = "#ec4899", t }) => {
  if (!Array.isArray(data) || data.length === 0) return <div className="text-gray-500 text-xs">{t('myListings.property.metrics.noData')}</div>;

  const max = Math.max(...data, 1);
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = 100 - (val / max) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 100 100" className="w-full h-8">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
};

const PropertyCard = React.memo(({ property, onEdit, onDeleteClick }) => {
  const { t } = useTranslation('MyListingDashboard'); 


  // Mock trend data
  const viewTrend = useMemo(() => 
    Array.from({ length: 7 }, (_, i) => 
      Math.max(0, property.views_count - Math.floor(Math.random() * 20) + i * 2)
    ), [property.views_count]
  );

  return (
    <div className="bg-[var(--bg-black)] border border-[var(--border)] rounded-xl overflow-hidden transition-all hover:border-pink-500/30">
      <div className="h-40 bg-gray-800 relative">
        {property.images?.[0]?.image ? (
          <img
            src={property.images[0].image}
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            {t('myListings.property.noImage')}
          </div>
        )}
        <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
          {property.available 
            ? t('myListings.property.status.available') 
            : t('myListings.property.status.rented')}
        </div>
      </div>

      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h4 className="font-bold text-white line-clamp-1">{property.title}</h4>
            <p className="text-sm text-gray-400 mt-1">
              {property.sector}, {property.city}
            </p>
            <p className="text-lg font-bold text-pink-400 mt-2">
              {property.price_rwf.toLocaleString()} RWF
            </p>
          </div>
          <div className="flex flex-col gap-1 ml-2">
            <button
              onClick={onDeleteClick}
              className="p-2 rounded bg-[var(--bg-black)] border-none text-red-400 hover:bg-red-500/30"
              aria-label={t('myListings.actions.ariaLabels.delete')}
            >
              <FaTrash size={14} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-4 text-center">
          <div className="bg-[var(--bg-black)] p-2 rounded-full">
            <FaEye className="mx-auto text-[var(--text-black)]" size={14} />
            <span className="text-xs block mt-1 text-white">{property.views_count}</span>
          </div>
          <div className="bg-[var(--bg-black)] p-2 rounded">
            <FaHeart className="mx-auto text-[var(--text-black)]" size={14} />
            <span className="text-xs block mt-1 text-white">{property.likes_count}</span>
          </div>
          <div className="bg-[var(--bg-black)] p-2 rounded">
            <FaComment className="mx-auto text-[var(--text-black)]" size={14} />
            <span className="text-xs block mt-1 text-white">{property.comments_count}</span>
          </div>
          <div className="bg-[var(--bg-black)] p-2 rounded">
            <FaShare className="mx-auto text-[var(--text-black)]" size={14} />
            <span className="text-xs block mt-1 text-white">{property.shares_count || 0}</span>
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
            <LineChart size={12} /> {t('myListings.property.metrics.viewsTrend')}
          </div>
          <Sparkline 
            data={viewTrend} 
            color="#ec4899"
            t={t}
          />
        </div>
      </div>
    </div>
  );
});

const MyListingsDashboard = ({ onClose, onAdd }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, views: 0, likes: 0, followers: 0, following: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { t } = useTranslation('MyListingDashboard');

  
  const fetchListings = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    const csrfToken = await getCSRFToken();
    
    // Fetch BOTH listings AND profile (for follower counts)
    const [listingsRes, profileRes] = await Promise.all([
      fetch(`${API_BASE_URL}/properties/my_properties/`, {
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Accept': 'application/json' },
      }),
      fetch(`${API_BASE_URL}/get_user_profile/`, { 
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Accept': 'application/json' },
      })
    ]);

    // Handle listings
    if (!listingsRes.ok) throw new Error('Failed to load listings');
    const listingsData = await listingsRes.json();
    const items = Array.isArray(listingsData) ? listingsData : (listingsData.results || []);
    setListings(items);

    // Handle profile (follower counts)
    let followers = 0, following = 0;
    if (profileRes.ok) {
      const profileData = await profileRes.json();
      followers = profileData.followers_count || 0;
      following = profileData.following_count || 0;
    }

    // Calculate stats
    const total = items.length;
    const active = items.filter(p => p.available).length;
    const views = items.reduce((sum, p) => sum + (p.views_count || 0), 0);
    const likes = items.reduce((sum, p) => sum + (p.likes_count || 0), 0);
    
    setStats({ total, active, views, likes, followers, following }); 
    
  } catch (err) {
    console.error('Fetch error:', err);
    setError(t('myListings.errors.loadFailed'));
    setStats({ total: 0, active: 0, views: 0, likes: 0, followers: 0, following: 0 });
  } finally {
    setLoading(false);
  }
}, [t]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleDeleteClick = (propertyId) => {
    setDeleteConfirm(propertyId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      const csrfToken = await getCSRFToken();
      const res = await fetch(`${API_BASE_URL}/properties/${deleteConfirm}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Accept': 'application/json' },
      });

      if (res.ok) {
        await fetchListings();
      } else {
        let details = '';
        try {
          const data = await res.json();
          details = data.detail || data.error || JSON.stringify(data);
        } catch {
          details = `status ${res.status}`;
        }
        console.error('Delete failed:', res.status, details);
        alert(`Failed to delete property: ${details || 'Please try again.'}`);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert(t('myListings.errors.generic'));
    } finally {
      setDeleteConfirm(null);
    }
  };


  return (
    <div 
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-black)] border border-[var(--border)] rounded-2xl w-full h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 border-b border-[var(--border)] gap-3">
          <div>
            <h2 className="text-xl font-bold text-pink-500">{t('myListings.title')}</h2>
            <div className="flex gap-4 mt-2 text-sm text-gray-400">
              <span>{t('myListings.stats.total', { count: stats.total })}</span>
              <span>{t('myListings.stats.active', { count: stats.active })}</span>
              <span>{t('myListings.stats.views', { count: stats.views.toLocaleString() })}</span>
              <span>{t('myListings.stats.followers', { count: stats.followers })}</span>
              <span>{t('myListings.stats.following', { count: stats.following })}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onAdd}
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white border-none px-4 py-2 rounded-lg text-sm font-medium transition"
            >
              <FaPlus size={14} /> {t('myListings.actions.addProperty')}
            </button>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-[var(--bg-black)]  border-[var(--border)] border-2 border-solid flex items-center justify-center text-pink-500 font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64 text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-pink-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-400">{error}</div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-block p-4 bg-[var(--bg-black)] rounded-full mb-4">
                <TrendingUp className="text-pink-500" size={32} />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">{t('myListings.empty.title')}</h3>
              <p className="text-gray-400 mb-4">{t('myListings.empty.description')}</p>
              <button
                onClick={onAdd}
                className="px-4 py-2 bg-pink-500 border-none text-white rounded-lg hover:bg-pink-600"
              >
                {t('myListings.empty.action')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
              {listings.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onEdit={onAdd}
                  onDeleteClick={() => handleDeleteClick(property.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div 
          onClick={(e) => e.target === e.currentTarget && setDeleteConfirm(null)}
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-pink-500 mb-2">{t('myListings.deleteConfirm.title')}</h3>
            <p className="text-gray-400 mb-4">
              {t('myListings.deleteConfirm.description')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-lg border border-[var(--border)] border-solid bg-[var(--bg-black)] text-[var(--text-black)]"
              >
                {t('myListings.deleteConfirm.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 py-2.5 bg-red-500 text-white border-none rounded-lg hover:bg-pink-600 transition"
              >
                {t('myListings.deleteConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyListingsDashboard;